// Admin audit (MUI, /admin/audit). Presentation port of dashboard/admin/AuditPage —
// same data (getAdminAudit), rebuilt with mui/* primitives + brainTokens.
import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { getAdminAudit } from "../../lib/brain-api";
import type { AuditView } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../../mui";

const c = brainTokens.color;
const STAT_KEYS = ["merges", "deprecations", "contradictionsFound", "pruned", "questionsOpened", "avgQuality"] as const;

function Sparkline({ points }: { points: number[] }) {
  if (!points.length) return null;
  const w = 240;
  const h = 40;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const d = points
    .map((p, i) => `${(i / Math.max(points.length - 1, 1)) * w},${h - ((p - min) / Math.max(max - min, 0.001)) * h}`)
    .join(" ");
  return (
    <Box component="svg" width={w} height={h} sx={{ display: "block", color: c.teal }}>
      <polyline fill="none" stroke="currentColor" strokeWidth={2} points={d} />
    </Box>
  );
}

export function AdminAuditPageMui() {
  const [v, setV] = useState<AuditView | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    getAdminAudit(30).then(setV).catch(() => setErr(true));
  }, []);

  if (err) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <PageHeader title="Audit" eyebrow="Nightly knowledge-audit results and trend" />
        <Box sx={{ fontSize: 13, color: c.dangerText, bgcolor: c.dangerBg, border: `1px solid ${c.dangerBorder}`, borderRadius: `${brainTokens.radius.panel}px`, px: 2, py: 1.25 }}>
          Couldn&apos;t load the audit report.
        </Box>
      </Box>
    );
  }
  if (!v) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <PageHeader title="Audit" eyebrow="Nightly knowledge-audit results and trend" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      </Box>
    );
  }

  const latest = (v.latest ?? {}) as Record<string, unknown>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <PageHeader title="Audit" eyebrow="Nightly knowledge-audit results and trend" />

      <Box sx={{ fontSize: 14, fontWeight: 700, color: c.ink, mt: 1 }}>Latest run</Box>
      {v.latest ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
          {STAT_KEYS.map((k) => (
            <KpiCard key={k} label={k} value={String(latest[k] ?? "—")} />
          ))}
        </Box>
      ) : (
        <EmptyState icon="clipboard-off" title="No audit run recorded yet." />
      )}

      <Box sx={{ fontSize: 14, fontWeight: 700, color: c.ink, mt: 1 }}>Quality trend</Box>
      <SectionCard padding={18}>
        {v.trend.length ? <Sparkline points={v.trend.map((t) => t.avgQuality)} /> : <EmptyState icon="chart-line" title="No trend yet." />}
      </SectionCard>

      <Box sx={{ fontSize: 14, fontWeight: 700, color: c.ink, mt: 1 }}>Recent runs</Box>
      {v.history.length === 0 ? (
        <EmptyState title="No recent runs." />
      ) : (
        <TableContainer sx={{ border: `1px solid ${c.border}`, borderRadius: `${brainTokens.radius.panel}px`, overflow: "hidden" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: c.panelBg }}>
                <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {v.history.map((h) => (
                <TableRow key={h.date} sx={{ "& + &": { borderTop: `1px solid ${c.borderLight}` } }}>
                  <TableCell>{h.date}</TableCell>
                  <TableCell>
                    <StatusPill tone={h.ok ? "success" : "danger"} label={h.ok ? "Ok" : "Error"} dot />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
